// based on https://letsbuildui.dev/articles/resizable-tables-with-react-and-css-grid / https://codesandbox.io/s/resizable-tables-rrj9m
// TODO: unnecessary save on load if id
/* eslint-disable no-trailing-spaces,no-loop-func */

import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { styled } from "@mui/material/styles";
import {
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from "@mui/material";
import { produce } from "immer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { useResizeDetector } from "react-resize-detector";
import TableCellContext from "./TableCellContext";
import ColumnEditor from "./ColumnEditor";

import api from "../../api";
import useStore from "../../store";
// eslint-disable-next-line import/no-cycle
import { PTMInput, PTMTooltip, PTMTooltipEllipsis } from "../index";
import VirtualizedScroll from "./VirtualizedScroll";
import StrictModeDroppable from "../StrictModeDroppable";

const StyledTable = styled(Table, {
    shouldForwardProp: (prop) => !["darkMode", "tableStyle", "options", "disabledHover"].includes(prop),
})(({
    theme, tableStyle, options, disabledHover,
}) => ({
    backgroundColor: "unset",
    "& .MuiTableRow-root:hover .MuiTableCell-root": disabledHover ? undefined : {
        opacity: 0.75,
    },
    ...(options.striped && {
        "& .MuiTableBody-root": {
            "& .MuiTableRow-root": {
                "&:nth-of-type(odd)": {
                    "& .MuiTableCell-root": {
                        backgroundColor: theme.palette.action.hover,
                    },
                },
            },
        },
    }),
    ...tableStyle,
}));

const generateColumnArray = (columns, currentColumns, defaultColumns) => {
    let tmpCurrentColumns = [];

    if (currentColumns && currentColumns.length > 0) {
        tmpCurrentColumns = [...currentColumns.filter((col) => !!columns[col.field])
            .map((obj) => ({ ...obj }))];
    } else {
        tmpCurrentColumns = defaultColumns.map((column) => ({
            field: column,
            enabled: true,
        }));
    }

    let columnId = 0;
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const column in columns) {
        columnId++;

        const tmpColumn = tmpCurrentColumns.find((currentColumn) => column === currentColumn.field);

        if (tmpColumn) {
            tmpColumn.id = columnId;
            tmpColumn.title = columns[column].title;
            tmpColumn.required = !!columns[column].required;
            tmpColumn.sort = columns[column].sort;
            tmpColumn.sortIcon = columns[column].sortIcon;
        } else {
            tmpCurrentColumns.push({
                id: columnId,
                field: column,
                title: columns[column].title,
                enabled: false,
                required: !!columns[column].required,
                sort: columns[column].sort,
                sortIcon: columns[column].sortIcon,
            });
        }
    }

    Object.keys(columns)
        .forEach((key) => {
            const column = columns[key];
            const tmpColumn = tmpCurrentColumns.find((obj) => obj.field === key);

            if (column.width === "auto") {
                tmpColumn.width = "auto";
            }

            if (column.fixedWidth) {
                tmpColumn.fixedWidth = true;
                tmpColumn.width = column.width;
            }
        });

    return tmpCurrentColumns;
};

/**
 * PTMTable!!! TODO:...
 */

function ActionButton(props) {
    const {
        icon,
        action,
        clickType,
        color,
        disable,
        title,
        tooltipTitle,
    } = props;

    const clickHandler = (e) => {
        e.stopPropagation();
        action(e);
    };

    return (
        <PTMTooltip title={tooltipTitle || title}>
            <Button
                disabled={disable}
                sx={{ width: "100%" }}
                color={color}
                onClick={clickType !== "mouse-down" ? clickHandler : () => {}}
                onMouseDown={clickType === "mouse-down" ? clickHandler : () => {}}
                variant="contained"
                size="small"
            >
                <FontAwesomeIcon icon={icon} />
            </Button>
        </PTMTooltip>
    );
}

const Row = React.memo(({
    row,
    i,
    disabledActions,
    isOpaque,
    options,
    onRowClickHandler,
    onRowContextMenuHandler,
    headers,
    rowStyle,
    disabledEllipsis,
    actions,
    expandRow,
    top,
}) => (
    <>
        <TableRow
            key={row.id}
            sx={{
                cursor: "pointer",
                ...disabledActions && {
                    cursor: "not-allowed",
                },
                ...isOpaque(row) && {
                    "& td": {
                        opacity: 0.5,
                    },
                },
            }}
            style={{
                display: "grid",
                ...(!options.fullWidth && { maxWidth: 0 }),
                minHeight: 20.2,
                position: "absolute",
                top,
                gridTemplateColumns: "inherit",
                width: "100%",
            }}
            onClick={() => onRowClickHandler(row)}
            onContextMenu={(e) => onRowContextMenuHandler(e, row)}
        >
            {headers.map((header) => (
                <TableCell
                    key={header.title}
                    sx={{
                        minWidth: 0,
                        color: "inherit",
                        backgroundColor: "inherit",
                        ...header.style,
                        ...rowStyle(row, i),
                    }}
                >
                    <PTMTooltipEllipsis
                        disabled={disabledEllipsis || (header.action && (header.action.type !== "select" || header.action.type !== "checkbox"))}
                    >
                        {header.action && (
                            header.action.type === "select" || header.action.type === "checkbox"
                                ? (
                                    <>
                                        {header.action.type === "select" && (
                                            <PTMInput
                                                noForm
                                                type="select"
                                                value={row.state}
                                                onChange={(e) => actions[header.action.name](row, e.target.value)}
                                                options={header.action.options}
                                                optionsValueType="key"
                                                optionsKey="id"
                                                optionsName="name"
                                                fullWidth
                                            />
                                        )}
                                        {header.action.type === "checkbox" && (
                                            <Checkbox
                                                sx={header.action.style}
                                                onChange={(e) => actions[header.action.name](row, e.target.checked, header.action.param)}
                                                checked={!!row.isEdited}

                                            />
                                        )}
                                    </>
                                )
                                : (
                                    <ActionButton
                                        disable={disabledActions || (header.action.disable && header.action.disable(row))}
                                        icon={
                                            header.action.dynamicIcon ? header.action.dynamicIcon(row) : header.action.icon
                                        }
                                        title={header.title}
                                        tooltipTitle={header.action.tooltip}
                                        color={header.action.color}
                                        clickType={header.action.clickType}
                                        action={(e) => actions[header.action.name](row, header.action.param, e)}
                                    />
                                )
                        )}
                        {header.format ? header.format(row, i) : row[header.field]}
                    </PTMTooltipEllipsis>
                </TableCell>
            ))}
        </TableRow>
        {expandRow && expandRow(row, i)}
    </>
));

function PTMTable(props) {
    const {
        id,
        columns,
        defaultColumns,
        rows,
        options,
        style,
        tableStyle,
        expandRow,
        clickHandlersRefresher,
        onRowClick,
        onRowContextMenu,
        disabledHover,
        actions,
        bottomInfos,
        disabledActions,
        disabledEllipsis,
        sortable,
        reorderList,
        sx,
        datasets,
        CustomContent,
        sortedBy,
    } = props;

    const { pagination } = options;

    const rowStyle = React.useCallback((row, i) => ((options && options.rowStyle) ? options.rowStyle(row, i) : {}), [options]);

    const isOpaque = React.useCallback((row) => ((options && options.opaque) ? options.opaque(row) : false), [options]);
    const isHidden = React.useCallback((row) => ((options && options.isHidden) ? options.isHidden(row) : false), [options]);

    const [init, setInit] = useState(false);
    const [initHeaders, setInitHeaders] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const [columnEditorIsOpen, setColumnEditorIsOpen] = useState(false);
    const stickyTableElement = useRef(null);
    const stickyHeaderContainer = useRef(null);
    const tableElement = useRef(null);
    const headersElement = useRef(null);
    const virtualizeElement = React.useRef(null);
    const [currentColumns, setCurrentColumns] = useState([]);
    const [headers, setHeaders] = useState([]);
    const darkMode = useStore((store) => store.app.darkMode);

    // clickHandlerRefresher solves problems related to not updated props in onRowClick functions passed to PTMTABLE
    // from parent components
    const onRowClickHandler = React.useCallback((row, misc) => {
        if (onRowClick) {
            onRowClick(row, misc);
        }
    }, [clickHandlersRefresher]);

    const onRowContextMenuHandler = React.useCallback((e, row) => {
        if (onRowContextMenu) {
            onRowContextMenu(e, row);
        }
    }, []);

    const onScrollHandler = (e) => {
        if (stickyHeaderContainer?.current) {
            stickyHeaderContainer.current.scrollLeft = e.target.scrollLeft;
        }
    };

    const mouseDown = (index) => {
        if (!init) {
            setInit(true);
        }
        setActiveIndex(index);
    };

    const mouseMove = useCallback(
        (e) => {
            const gridColumns = headers.map((col, i) => {
                if (col.width === "auto") {
                    return "auto";
                }

                if (i === activeIndex) {
                    const width = (e.clientX) - (headersElement.current.children[i].getBoundingClientRect().left);
                    if (width < col.minWidth) {
                        return `${col.minWidth}px`;
                    }

                    if (width > col.maxWidth) {
                        return `${col.maxWidth}px`;
                    }

                    return `${width}px`;
                }
                return `${headersElement.current.children[i].offsetWidth}px`;
            });

            if (options.stickyHeader) {
                if (stickyTableElement?.current?.style) {
                    stickyTableElement.current.children[0].children[0].style.gridTemplateColumns = `${gridColumns.join(
                        " ",
                    )}`;
                }
            } else if (tableElement?.current?.style) {
                tableElement.current.children[0].children[0].style.gridTemplateColumns = `${gridColumns.join(
                    " ",
                )}`;
            }
            if (options.virtualization) {
                virtualizeElement.current.style.gridTemplateColumns = `${gridColumns.join(
                    " ",
                )}`;
            } else if (tableElement.current.children[1]) {
                [...tableElement.current.children[1].children].forEach((child) => {
                    child.style.gridTemplateColumns = `${gridColumns.join(
                        " ",
                    )}`;
                });
            }
        },
        [activeIndex, headers],
    );

    const removeListeners = useCallback(() => {
        window.removeEventListener("mousemove", mouseMove);
        window.removeEventListener("mouseup", removeListeners);
    }, [mouseMove]);

    const mouseUp = useCallback(() => {
        setActiveIndex(null);
        removeListeners();
    }, [setActiveIndex, removeListeners]);

    useEffect(() => {
        if (activeIndex !== null) {
            window.addEventListener("mousemove", mouseMove);
            window.addEventListener("mouseup", mouseUp);
        }

        return () => {
            removeListeners();
        };
    }, [activeIndex, mouseMove, mouseUp, removeListeners]);

    const handleHeaderContextSelect = (action) => {
        if (action === "editColumns") {
            setColumnEditorIsOpen(true);
        }
    };

    const generateHeaders = (newCurrentColumns) => newCurrentColumns.filter((column) => column.enabled)
        .map((column) => ({
            field: column.field,
            ...columns[column.field],
            width: column?.width || columns?.[column.field]?.width,
            fixedWidth: column.fixedWidth,
            sort: column.sort,
            sortIcon: column.sortIcon,
        }));

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const items = reorder(
            rows,
            result.source.index,
            result.destination.index,
        );
        reorderList(items);
    };

    useEffect(() => {
        if (currentColumns) {
            if (!initHeaders) {
                setHeaders(generateHeaders(currentColumns));
                setInitHeaders(true);
            } else if (id) {
                (async () => {
                    await api.ptmTables.save({
                        id,
                        columns: currentColumns,
                    });
                })();
            }
        }
    }, [currentColumns]);

    const updateCurrentColumns = (newCurrentColumns) => {
        setCurrentColumns(newCurrentColumns);
        setHeaders(generateHeaders(newCurrentColumns));
    };

    useEffect(() => {
        const gridColumns = headers.map((col) => {
            if (col.width === "auto") {
                return col.width;
            }
            if (col.gridWidth) {
                return `${col.gridWidth}fr`;
            }
            return `${col.width}px`;
        });

        if (tableElement.current.children.length > 0) {
            [...tableElement.current.children[0].children].forEach((child) => {
                child.style.gridTemplateColumns = `${gridColumns.join(
                    " ",
                )}`;
            });

            if (options.stickyHeader) {
                if (stickyTableElement?.current?.style) {
                    stickyTableElement.current.children[0].children[0].style.gridTemplateColumns = `${gridColumns.join(
                        " ",
                    )}`;
                }
            } else if (tableElement?.current?.style) {
                tableElement.current.children[0].children[0].style.gridTemplateColumns = `${gridColumns.join(
                    " ",
                )}`;
            }
        }

        if (options.virtualization) {
            virtualizeElement.current.style.gridTemplateColumns = `${gridColumns.join(
                " ",
            )}`;
        } else if (tableElement.current.children[1]) {
            [...tableElement.current.children[1].children].forEach((child) => {
                child.style.gridTemplateColumns = `${gridColumns.join(
                    " ",
                )}`;
            });
        }
    }, [headers, rows]);

    useEffect(() => {
        if (activeIndex === null && init) {
            let widths;

            if (options.stickyHeader) {
                widths = stickyTableElement.current?.children[0].children[0].style
                    .gridTemplateColumns.split(" ")
                    .map((col) => col.replace("px", ""));
            } else {
                widths = tableElement.current?.children[0].children[0].style
                    .gridTemplateColumns.split(" ")
                    .map((col) => col.replace("px", ""));
            }

            const tmpColumns = generateColumnArray(columns, produce(currentColumns, (draft) => {
                widths.forEach((width, index) => {
                    draft.filter((item) => item.enabled)[index].width = width;
                });
            }), defaultColumns);

            setCurrentColumns(tmpColumns);
            setHeaders(generateHeaders(tmpColumns));
        }
    }, [activeIndex, init]);

    useEffect(() => {
        if (id) {
            (async () => {
                try {
                    setInitHeaders(false);
                    const { data } = await api.ptmTables.find(id);

                    const tmpColumns = generateColumnArray(columns, data.columns, defaultColumns);
                    setCurrentColumns(tmpColumns);
                    setHeaders(generateHeaders(tmpColumns));
                } catch (e) {
                    const tmpColumns = generateColumnArray(columns, [], defaultColumns);

                    setCurrentColumns(tmpColumns);
                    setHeaders(generateHeaders(tmpColumns));
                }
            })();
        } else {
            const tmpColumns = generateColumnArray(columns, [], defaultColumns);
            setCurrentColumns(tmpColumns);
            setHeaders(generateHeaders(tmpColumns));
        }
    }, [id, columns]);

    const { height, ref } = useResizeDetector({
        handleHeight: options.virtualization?.handleHeight ?? true,
        refreshMode: "debounce",
        refreshRate: 1000,
    });
    return (
        <div style={style} ref={options.virtualization && ref}>
            {columnEditorIsOpen && (
                <ColumnEditor
                    id={id}
                    setCurrentColumns={(newCurrentColumns) => updateCurrentColumns(newCurrentColumns)}
                    currentColumns={currentColumns}
                    close={() => setColumnEditorIsOpen(false)}
                />
            )}
            {options.stickyHeader && (
                <TableContainer
                    sx={sx}
                    ref={stickyHeaderContainer}
                    style={{ overflow: "hidden" }}
                >
                    <Table
                        ref={stickyTableElement}
                        style={{
                            width: "100%",
                            display: "grid",
                        }}
                    >
                        <TableHead style={{ display: "contents" }}>
                            <TableRow
                                style={{
                                    display: "grid",
                                    minHeight: 20.2,
                                }}
                                ref={headersElement}
                            >
                                {headers.map((header, index) => (
                                    <TableCellContext
                                        key={header.title}
                                        ref={header.ref}
                                        style={{
                                            minWidth: 0,
                                            position: "relative",
                                            ...header.style,
                                        }}
                                        field={header.field}
                                        disableContext={!id}
                                        onClick={() => header.sort(sortedBy)}
                                        onContextSelect={handleHeaderContextSelect}
                                    >
                                        {/* > */}
                                        {header.icon ? (
                                            <PTMTooltip title={header.title}>
                                                {header.iconTitle
                                                    ? (
                                                        <div>
                                                            <FontAwesomeIcon
                                                                icon={header.icon}
                                                                style={{ marginRight: 2 }}
                                                            />
                                                            {header.title}
                                                        </div>
                                                    )
                                                    : (
                                                        <FontAwesomeIcon
                                                            icon={header.icon}
                                                        />
                                                    )}
                                            </PTMTooltip>
                                        ) : (
                                            <PTMTooltipEllipsis>
                                                {header.title}
                                            </PTMTooltipEllipsis>
                                        )}
                                        {header.sort && (
                                            <FontAwesomeIcon
                                                icon={header.getSortIcon(sortedBy)}
                                                style={{
                                                    position: "absolute",
                                                    right: "7px",
                                                    top: "4px",
                                                }}
                                            />
                                        )}
                                        {!header.fixedWidth && header.width !== "auto" && (
                                            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                                            <div
                                                style={{
                                                    display: "block",
                                                    position: "absolute",
                                                    cursor: "col-resize",
                                                    width: 7,
                                                    right: 0,
                                                    top: 0,
                                                    zIndex: 1,
                                                    borderRight: `2px solid rgb(${darkMode ? "255 255 255" : "0 0 0 "} / 25%)`,
                                                    height: "100%",
                                                }}
                                                onMouseDown={() => mouseDown(index)}
                                            />
                                        )}
                                    </TableCellContext>
                                ))}
                            </TableRow>
                        </TableHead>
                    </Table>
                </TableContainer>
            )}
            <TableContainer
                onScroll={onScrollHandler}
                style={{
                    height: options?.stickyHeader ? "calc(100% - 20px)" : ((options?.enablePagination) ? "calc(100% - 22px)" : "100%"),
                    overflow: options?.virtualization && "auto hidden",
                }}
                sx={sx}
            >
                {useMemo(() => (
                    <StyledTable
                        ref={tableElement}
                        style={{
                            width: "100%",
                            display: "grid",
                            alignItems: "start",
                            height: options.virtualization && "100%",
                        }}
                        tableStyle={tableStyle}
                        options={options}
                        disabledHover={disabledHover}
                    >
                        {!options.stickyHeader && (
                            <TableHead style={{ display: "grid" }}>
                                <TableRow
                                    style={{
                                        display: "grid",
                                        minHeight: 20.2,
                                    }}
                                    ref={headersElement}
                                >
                                    {headers.map((header, index) => (
                                        <TableCellContext
                                            key={header.title}
                                            ref={header.ref}
                                            style={{
                                                minWidth: 0,
                                                position: "relative",
                                                ...header.style,
                                            }}
                                            field={header.field}
                                            disableContext={!id}
                                            onClick={() => header.sort(sortedBy)}
                                            onContextSelect={handleHeaderContextSelect}
                                        >
                                            {/* <HeaderContext */}
                                            {/*    field={header.field} */}
                                            {/*    onContextSelect={handleHeaderContextSelect} */}
                                            {/* > */}
                                            {header.icon ? (
                                                <PTMTooltip title={header.title}>
                                                    <FontAwesomeIcon
                                                        icon={header.icon}
                                                    />
                                                </PTMTooltip>
                                            ) : (
                                                <PTMTooltipEllipsis>
                                                    {header.title}
                                                </PTMTooltipEllipsis>
                                            )}
                                            {header.sort && (
                                                <FontAwesomeIcon
                                                    icon={header.getSortIcon(sortedBy)}
                                                    style={{
                                                        position: "absolute",
                                                        right: "7px",
                                                        top: "4px",
                                                    }}
                                                />
                                            )}
                                            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                                            <div
                                                style={{
                                                    display: "block",
                                                    position: "absolute",
                                                    cursor: header.width === "auto" || header.fixedWidth ? "default" : "col-resize",
                                                    width: 7,
                                                    right: 0,
                                                    top: 0,
                                                    zIndex: 1,
                                                    borderRight: `2px solid rgb(${darkMode ? "255 255 255" : "0 0 0 "} / 25%)`,
                                                    height: "100%",
                                                }}
                                                onMouseDown={() => mouseDown(index)}
                                            />
                                            {/* </HeaderContext> */}
                                        </TableCellContext>
                                    ))}
                                </TableRow>
                            </TableHead>
                        )}
                        {sortable ? (
                            <DragDropContext onDragEnd={onDragEnd}>
                                <StrictModeDroppable droppableId="ptm-table">
                                    {(provided) => (
                                        <TableBody
                                            style={
                                                { display: "grid" }
                                            }
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            {rows.map((row, i) => {
                                                const idLabel = row.ata_id ? "ata_id" : "id";
                                                return (
                                                    !isHidden(row) && (
                                                        <Draggable
                                                            key={row[idLabel]}
                                                            index={i}
                                                            draggableId={row[idLabel]?.toString()}
                                                        >
                                                            {(providedRow) => (
                                                                <TableRow
                                                                    ref={providedRow.innerRef}
                                                                    {...providedRow.draggableProps}
                                                                    {...providedRow.dragHandleProps}
                                                                    key={row.id}
                                                                    // hover={!disabledHover}
                                                                    sx={{
                                                                        display: "grid",
                                                                        cursor: "pointer",
                                                                        minHeight: 20.2,
                                                                        ...disabledActions && {
                                                                            cursor: "not-allowed",
                                                                        },
                                                                        ...isOpaque(row) && {
                                                                            "& td": {
                                                                                opacity: 0.5,
                                                                            },
                                                                        },
                                                                    }}
                                                                    style={{
                                                                        ...providedRow.draggableProps.style,
                                                                        // top: "auto !important",
                                                                        // left: "auto !important",
                                                                        // position: "static",
                                                                        marginTop: providedRow.draggableProps.style.position === "fixed" ? -45 : undefined,
                                                                        left: 0,
                                                                    }}
                                                                    onClick={() => onRowClickHandler(row)}
                                                                    onContextMenu={(e) => onRowContextMenuHandler(e, row)}
                                                                >
                                                                    {headers.map((header) => (
                                                                        <TableCell
                                                                            key={header.title}
                                                                            sx={{
                                                                                minWidth: 0,
                                                                                display: "grid",
                                                                                color: "inherit",
                                                                                backgroundColor: "inherit",
                                                                                ...header.style,
                                                                                ...rowStyle(row, i),
                                                                            }}
                                                                        >
                                                                            <PTMTooltipEllipsis disabled={!!header.action}>
                                                                                {
                                                                                    header.action
                                                                                    && (
                                                                                        <ActionButton
                                                                                            disable={disabledActions}
                                                                                            icon={
                                                                                                header.action.dynamicIcon ? header.action.dynamicIcon(row) : header.action.icon
                                                                                            }
                                                                                            title={header.title}
                                                                                            color={header.action.color}
                                                                                            clickType={header.action.clickType}
                                                                                            action={(e) => actions[header.action.name](row, header.action.param, e)}
                                                                                        />
                                                                                    )
                                                                                }
                                                                                {header.format ? header.format(row, i) : row[header.field]}
                                                                            </PTMTooltipEllipsis>
                                                                        </TableCell>
                                                                    ))}
                                                                </TableRow>
                                                            )}
                                                        </Draggable>
                                                    )
                                                );
                                            })}
                                            {provided.placeholder}
                                        </TableBody>
                                    )}
                                </StrictModeDroppable>
                            </DragDropContext>
                        )
                            : (
                                <TableBody
                                    style={{
                                        width: "100%",
                                        display: "grid",
                                    }}
                                >
                                    {CustomContent && (rows.map((row, i) => (
                                        <CustomContent
                                            key={row.id}
                                            i={i}
                                            onClick={onRowClickHandler}
                                            disabledEllipsis={disabledEllipsis}
                                            headers={headers}
                                            {...(datasets && datasets.filter((set) => set.id === row.id).length !== 0) && { datasets: datasets.filter((set) => set.id === row.id) }}
                                            row={row}
                                            options={options}
                                        />
                                    )))}
                                    {!CustomContent && !options.virtualization && (rows).map((row, i) => (
                                        !isHidden(row) && (
                                            <React.Fragment
                                                key={row.id}
                                            >
                                                <TableRow
                                                    sx={{
                                                        cursor: "pointer",
                                                        ...disabledActions && {
                                                            cursor: "not-allowed",
                                                        },
                                                        ...isOpaque(row) && {
                                                            "& td": {
                                                                opacity: 0.5,
                                                            },
                                                        },
                                                    }}
                                                    style={{
                                                        display: "grid",
                                                        ...(!options.fullWidth && { maxWidth: 0 }),
                                                        minHeight: 20.2,
                                                    }}
                                                    onClick={() => onRowClickHandler(row)}
                                                    onContextMenu={(e) => onRowContextMenuHandler(e, row)}
                                                >
                                                    {headers.map((header) => (
                                                        <TableCell
                                                            key={header.title}
                                                            sx={{
                                                                minWidth: 0,
                                                                color: "inherit",
                                                                backgroundColor: "inherit",
                                                                ...header.style,
                                                                ...rowStyle(row, i),
                                                            }}
                                                        >
                                                            <PTMTooltipEllipsis
                                                                disabled={disabledEllipsis || (header.action && (header.action.type !== "select" || header.action.type !== "checkbox"))}
                                                            >
                                                                {header.action && (
                                                                    header.action.type === "select" || header.action.type === "checkbox" ? (
                                                                        <>
                                                                            {header.action.type === "select" && (
                                                                                <PTMInput
                                                                                    noForm
                                                                                    type="select"
                                                                                    value={row.state}
                                                                                    onChange={(e) => actions[header.action.name](row, e.target.value)}
                                                                                    options={header.action.options}
                                                                                    optionsValueType="key"
                                                                                    optionsKey="id"
                                                                                    optionsName="name"
                                                                                    fullWidth
                                                                                />
                                                                            )}
                                                                            {header.action.type === "checkbox" && (
                                                                                <Checkbox
                                                                                    sx={header.action.style}
                                                                                    onChange={(e) => actions[header.action.name](row, e.target.checked, header.action.param)}
                                                                                    checked={!!row.isEdited}

                                                                                />
                                                                            )}
                                                                        </>
                                                                    ) : (!(header.action.hide && header.action.hide(row)) && (
                                                                        <ActionButton
                                                                            disable={disabledActions || (header.action.disable && header.action.disable(row))}
                                                                            icon={
                                                                                header.action.dynamicIcon ? header.action.dynamicIcon(row) : header.action.icon
                                                                            }
                                                                            title={header.title}
                                                                            tooltipTitle={header.action.tooltip}
                                                                            color={header.action.color}
                                                                            clickType={header.action.clickType}
                                                                            action={(e) => actions[header.action.name](row, header.action.param, e)}
                                                                        />
                                                                    ))
                                                                )}
                                                                {header.format ? header.format(row, i) : row[header.field]}
                                                            </PTMTooltipEllipsis>
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                                {expandRow && expandRow(row, i)}
                                            </React.Fragment>
                                        )
                                    ))}
                                    {!CustomContent && options.virtualization && (
                                        <VirtualizedScroll
                                            height={height}
                                            rows={rows}
                                            Row={Row}
                                            options={options}
                                            element={virtualizeElement}
                                            actions={actions}
                                            disabledActions={disabledActions}
                                            isOpaque={isOpaque}
                                            rowStyle={rowStyle}
                                            onRowClickHandler={onRowClickHandler}
                                            clickHandlersRefresher={clickHandlersRefresher}
                                            onRowContextMenuHandler={onRowContextMenuHandler}
                                            headers={headers}
                                            disabledEllipsis={disabledEllipsis}
                                            expandRow={expandRow}
                                        />
                                    )}
                                </TableBody>
                            )}
                    </StyledTable>
                ), [headers, rows, handleHeaderContextSelect])}
            </TableContainer>
            {pagination?.enabled && !options.virtualization && (
                <>
                    {bottomInfos && (
                        <span style={{
                            float: "right",
                            paddingRight: 6,
                        }}
                        >
                            {bottomInfos}
                        </span>
                    )}
                    <TablePagination
                        rowsPerPageOptions={[]}
                        component="div"
                        count={pagination.count}
                        rowsPerPage={pagination.rowsPerPage}
                        page={pagination.page}
                        onPageChange={pagination.onChangePage}
                        showFirstButton={pagination.showFirstButton}
                        showLastButton={pagination.showLastButton}
                        ActionsComponent={pagination.ActionsComponent}
                        labelDisplayedRows={pagination.labelDisplayedRows}
                    />
                </>
            )}
        </div>
    );
}

// id, columns, defaultColumns, rows, options, style, onRowClick
PTMTable.propTypes = {
    /**
     * TODO:...
     */
    // id: PropTypes.string.isRequired,
    /**
     * TODO:...
     */
    // TODO: fix
    // eslint-disable-next-line react/forbid-prop-types
    columns: PropTypes.objectOf(PropTypes.object).isRequired,
    /**
     * TODO:...
     */
    defaultColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
    /**
     * TODO:...
     */
    // TODO: fix
    // eslint-disable-next-line react/forbid-prop-types
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    /**
     * TODO:...
     */
    options: PropTypes.shape({}),
};

PTMTable.defaultProps = {
    options: {},
};

export default React.memo(PTMTable);
